import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ApplicantType } from "shared/types";
import { Loader } from "shared/components/Loader";
import { instance } from "../../common/api";
// import { LinkInactiveView } from "../../views/404/link-inactive";
import type { PartnerConfig } from "../../common/providers/layout";
import { getPartnerConfig } from "../../common/providers/layout/utils";
import { Redirect } from "../../common/components/Redirect";
import { useGoogleAnalytics } from "../../common/providers/google-analytics";

const AuthTokenPage = () => {
  const { setGaId } = useGoogleAnalytics();
  const { query, push } = useRouter();

  const [token, setToken] = useState<string>();
  const mounted = useRef<boolean>(false);

  useEffect(() => {
    if (query.token) setToken(query.token as string);
    mounted.current = true;
  }, [query.token]);

  useEffect(() => {
    const gclid = query.gclid as string;
    const fbclid = query.fbclid as string;
    const restparams = Object.entries(query).filter(
      ([key]) => key !== "gclid" && key !== "fbclid"
    );

    if (gclid) localStorage.setItem("gclid", gclid);

    if (fbclid) localStorage.setItem("fbclid", fbclid);

    localStorage.setItem("restparams", JSON.stringify(restparams));
  }, [query.gclid, query.fbclid]);

  const { data: partner } = useQuery<PartnerConfig>({
    queryKey: getPartnerConfig(`${token}_inner`),
    queryFn: () =>
      instance<{
        partner: PartnerConfig;
      }>(`/screen/magic_links/partner`, {
        params: {
          token
        }
      }).then(({ data: _data }) => {
        if (_data.partner.google_analytics_id)
          setGaId(_data.partner.google_analytics_id);

        return _data.partner;
      }),
    retry: false
  });

  const { error } = useQuery({
    queryKey: ["CHECK_MAGIC_LINK", token],
    queryFn: () =>
      instance(`/screen/magic_links/check`, {
        params: {
          token: query.token
        }
      }).then(
        (magicLinkData: {
          data: {
            magic_link: {
              email: string | null;
              phone: string | null;
              role: null | ApplicantType;
              active: boolean;
              is_public: boolean;

              unit_id: string | null;

              // Used to check if the magic link is used. Only for individual link.
              // If has been used: Button to login or sign up.
              used: boolean;
            };
          };
        }) => {
          sessionStorage.setItem("ml_token", String(query.token));
          if (!magicLinkData.data.magic_link.active) {
            setToken("");
            push("/");
            return magicLinkData.data.magic_link;
          }

          if (magicLinkData.data.magic_link.is_public) {
            push(`/units?token=${token}`);
            return magicLinkData.data.magic_link;
          }

          push(
            `/auth/sign-in?token=${token}${magicLinkData.data.magic_link.unit_id ? `&unitId=${magicLinkData.data.magic_link.unit_id}` : ""}`
          );
          return magicLinkData.data.magic_link;
        }
      ),
    retry: false,
    enabled: Boolean(token && mounted.current)
  });

  const theme = useMemo(() => {
    const ret = {};
    if (partner?.colors?.primary && Object.keys(partner.colors.primary).length)
      Object.entries(partner.colors.primary).forEach(([key, value]) => {
        ret[`--primary-${key}`] = value;
      });

    if (partner?.base_border_radius)
      ret["--base-border-radius"] = partner.base_border_radius;

    return ret;
  }, [partner]);

  return (
    <div style={theme}>
      {!mounted.current ? (
        <div className="flex h-screen w-full items-center justify-center bg-background-900">
          <Loader />
        </div>
      ) : error ? (
        // TODO: likely need a backend to diversify.
        // We don't need to redirect and we must show error.
        // This only to satisfy the double click on private magic link.
        //
        // This component must be used instead
        // <LinkInactiveView />
        <Redirect to="/" skipHistory />
      ) : (
        <div className="flex h-screen w-full items-center justify-center bg-background-900">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default AuthTokenPage;
